import { ReactNode } from "react";
import { Trans } from "react-i18next";
import { TranslationStringType } from "@localization/LocalizationContext";
import { RiskTreatmentType } from "../riskTreatmentType";
import { RiskScenarioItem } from "./RiskAssessmentsOverview.types";

// The drift warning is a two-part text: a factual reconciliation of the anchor vs current level/date (kept
// explicit so the cross-revision relationship isn't merely implied), followed by the case-specific action
// sentence. The action stays per-case because reconfirm is only offered when the acceptance is still
// comparable — worsened/scale-changed acceptances require a fresh acceptance, not a reconfirm.
// The risk-level labels in the facts are bolded (the facts strings wrap them in <b>), so the result is a
// ReactNode. Shared so the assessments-overview tooltip, the accept dialog and the mitigation plan dialog
// show identical wording.
function getDriftActionKey(scenario: RiskScenarioItem): string {
    if (scenario.treatmentType !== RiskTreatmentType.Accept) return "riskTreatmentDriftPlan";
    if (scenario.acceptanceScaleChanged) return "riskTreatmentDriftScaleChanged";
    if (scenario.acceptanceWorsened) return "riskTreatmentDriftWorsened";
    return "riskTreatmentDriftReconfirmable";
}

export function getDriftWarningText(scenario: RiskScenarioItem, translateString: TranslationStringType, translateDate: (date: Date) => string): ReactNode {
    const action = translateString(getDriftActionKey(scenario));
    if (!scenario.driftAnchorRiskLabel || !scenario.driftAnchorDate) return action;

    const factsKey = scenario.treatmentType === RiskTreatmentType.Accept ? "riskTreatmentDriftAcceptedFacts" : "riskTreatmentDriftPlanFacts";
    return (
        <>
            <Trans
                i18nKey={factsKey}
                components={{ b: <strong /> }}
                values={{
                    anchorLevel: scenario.driftAnchorRiskLabel,
                    anchorDate: translateDate(new Date(scenario.driftAnchorDate)),
                    currentLevel: scenario.riskLabel,
                    currentDate: scenario.assessedAt ? translateDate(new Date(scenario.assessedAt)) : "",
                }}
            />{" "}
            {action}
        </>
    );
}

// The completed-mitigation-stale counterpart of the drift text: a completed plan still sits as the current
// treatment but the risk was reassessed since, so the same anchor-vs-current reconciliation is shown, followed
// by the "create a new mitigation" action. Reuses the driftAnchor* fields (the plan's anchor level + date).
export function getCompletedMitigationStaleText(
    scenario: RiskScenarioItem,
    translateString: TranslationStringType,
    translateDate: (date: Date) => string
): ReactNode {
    const action = translateString("riskMitigationStaleAction");
    if (!scenario.driftAnchorRiskLabel || !scenario.driftAnchorDate) return action;

    return (
        <>
            <Trans
                i18nKey="riskMitigationStaleFacts"
                components={{ b: <strong /> }}
                values={{
                    anchorLevel: scenario.driftAnchorRiskLabel,
                    anchorDate: translateDate(new Date(scenario.driftAnchorDate)),
                    currentLevel: scenario.riskLabel,
                    currentDate: scenario.assessedAt ? translateDate(new Date(scenario.assessedAt)) : "",
                }}
            />{" "}
            {action}
        </>
    );
}
